import moment from 'moment'
import KnoxService from './KnoxService'
import StrappService from './StrappService'

/**
 * Service orchestrateur pour gérer les devices
 * Coordonne Knox (source) et Strapp (fournisseur)
 * Respecte l'ordre : Knox -> Strapp
 */
export default {
  /**
   * Récupère un device complet (Knox + Strapp) par IMEI
   * @param {string} imei - Numéro IMEI
   * @param {boolean} forceRefresh - Forcer le refresh du cache
   * @returns {Promise<Object>} Device complet avec données Knox et Strapp
   */
  getDeviceByImei(imei, forceRefresh = false) {
    return Promise.all([
      KnoxService.getDeviceByImei(imei, forceRefresh)
        .catch(error => {
          console.warn(`[DeviceService] Knox device not found for IMEI ${imei}:`, error)
          return null
        }),
      StrappService.getDevices({ imei, limit: 1, force: forceRefresh })
        .then(response => response.data && response.data.length > 0 ? response.data[0] : null)
        .catch(error => {
          console.warn(`[DeviceService] Strapp device not found for IMEI ${imei}:`, error)
          return null
        })
    ])
      .then(([knoxData, strappData]) => {
        return this._mergeDeviceData(imei, knoxData, strappData)
      })
  },

  /**
   * Récupère tous les devices d'un client
   * Utilise customer.idKnox et customer.idStrapp
   * @param {Object} customer - Objet customer avec idKnox et idStrapp
   * @param {Object} filters - Filtres additionnels
   * @returns {Promise<Array>} Liste des devices enrichis (Knox + Strapp)
   */
  getDevicesByClient(customer, filters = {}) {
    if (!customer) {
      return Promise.resolve([])
    }

    const knoxUserId = customer.idKnox
    const strappUserId = customer.idStrapp

    if (!knoxUserId && !strappUserId) {
      return Promise.resolve([])
    }

    const promises = []

    // 1. Charger les devices Knox si idKnox existe
    if (knoxUserId) {
      promises.push(
        KnoxService.getUserDevices(knoxUserId, filters.forceRefresh || false)
          .catch(() => [])
      )
    } else {
      promises.push(Promise.resolve([]))
    }

    // 2. Charger les devices Strapp si idStrapp existe
    if (strappUserId) {
      promises.push(
        StrappService.getUserDevices(strappUserId, { 
          populate: 'model,user,user.address,site,billingEntity',
          ...filters 
        })
          .then(response => response.results || [])
          .catch(() => [])
      )
    } else {
      promises.push(Promise.resolve([]))
    }

    // 3. Merger et enrichir
    return Promise.all(promises)
      .then(([knoxDevices]) => this.enrichDevicesWithStrapp(knoxDevices, filters.forceRefresh || false))
  },

  /**
   * Liste tous les devices Knox uniquement (Strapp chargé à la demande par client)
   * @param {Object} options - Options de filtrage
   * @param {number} options.limit - Limite résultats par page
   * @param {boolean} options.forceRefresh - Forcer refresh cache
   * @returns {Promise<Array>} Liste devices Knox
   */
  getAllDevices(options = {}) {
    const { limit = 100, forceRefresh = false } = options

    // Charger tous les devices Knox avec pagination
    return this._fetchAllKnoxDevices({ limit, forceRefresh })
      .then(knoxDevices => {
        // Retourner les devices Knox seuls (strappData = null)
        // Strapp sera chargé à la demande par client pour éviter le rate limit
        return knoxDevices.map(knoxDevice => ({
          imei: knoxDevice.imei,
          knoxDeviceId: knoxDevice.deviceId,
          strappDeviceId: null,
          knoxData: knoxDevice,
          strappData: null,
          status: this._getDeviceStatus(knoxDevice, null),
          model: knoxDevice.deviceModelKind || knoxDevice.deviceModel || null,
          user: this._getDeviceUser(knoxDevice, null),
          site: null,
          billingEntity: null,
          isEligibleForBuyback: false,
          isArchived: false,
          iccid: this._normalizeIccid(knoxDevice.iccid),
          lastUpdated: this._getLastUpdated(knoxDevice, null),
          createdAt: moment().format('YYYY-MM-DD'),
          updatedAt: moment().format('YYYY-MM-DD')
        }))
      })
      .catch(error => {
        console.error('[DeviceService] Error fetching Knox devices:', error)
        return []
      })
  },

  /**
   * Charge tous les devices Knox avec pagination
   * @private
   * @param {Object} options - Options
   * @returns {Promise<Array>} Tous les devices Knox
   */
  _fetchAllKnoxDevices(options = {}) {
    const { limit = 100, forceRefresh = false } = options
    const allDevices = []
    
    const fetchPage = (start = 0) => {
      return KnoxService.getDevices({ limit, start, force: forceRefresh })
        .then(response => {
          const devices = response.resultValue?.deviceList || []
          const total = response.resultValue?.total || 0
          
          allDevices.push(...devices)
          
          // Si on a chargé moins que la limite OU qu'on a tout, on arrête
          if (devices.length < limit || allDevices.length >= total) {
            return allDevices
          }
          
          // Sinon, charger la page suivante
          return fetchPage(start + limit)
        })
    }
    
    return fetchPage(0)
  },

  /**
   * Enrichit des devices Knox avec les données Strapp (par IMEI)
   * @param {Array} knoxDevices - Devices Knox à enrichir
   * @returns {Promise<Array>} Devices enrichis avec Strapp
   */
  enrichDevicesWithStrapp(knoxDevices, forceRefresh = false) {
    if (!knoxDevices || knoxDevices.length === 0) {
      return Promise.resolve([])
    }
    
    // Extraire les IMEI - gérer à la fois les devices bruts Knox et les devices déjà structurés
    const imeis = knoxDevices.map(d => {
      // Si le device a déjà knoxData (structure mergée), prendre l'IMEI de là
      if (d.knoxData) {
        return d.knoxData.imei
      }
      // Sinon, c'est un device brut de Knox API
      return d.imei
    }).filter(Boolean)
    
    
    // Charger les devices Strapp par IMEI (une requête par IMEI)
    return Promise.all(
      imeis.map(imei => 
        StrappService.getDevices({ 
          imei, 
          limit: 1,
          populate: 'user,user.address,model,site,billingEntity',
          force: forceRefresh
        })
          .then(response => {
            const device = response.results?.[0] || null
            return { imei, strappDevice: device }
          })
          .catch(error => {
            console.error(`  ❌ Error fetching Strapp device for IMEI ${imei}:`, error.message)
            return { imei, strappDevice: null }
          })
      )
    )
      .then(strappResults => {
        // Créer un map IMEI -> strappDevice
        const strappMap = new Map()
        strappResults.forEach(({ imei, strappDevice }) => {
          if (strappDevice) {
            strappMap.set(imei, strappDevice)
          }
        })
        
        // Merger avec les devices Knox
        return knoxDevices.map(knoxDevice => {
          // Extraire les données Knox brutes
          const knoxData = knoxDevice.knoxData || knoxDevice
          const imei = knoxData.imei
          const strappDevice = strappMap.get(imei)
          
          // Merger avec _mergeDeviceData
          return this._mergeDeviceData(imei, knoxData, strappDevice)
        })
      })
  },

  /**
   * Charge les devices pour une liste d'users par batch
   * @private
   * @param {Array} users - Liste des users Strapp
   * @param {number} batchSize - Taille du batch (défaut: 50 pour profiter du rate limit 1000 req/min)
   * @returns {Promise<Array>} Tous les devices
   */
  _fetchDevicesForUsersBatch(users, batchSize = 50) {
    const allDevices = []
    
    const processBatch = (startIndex) => {
      const batch = users.slice(startIndex, startIndex + batchSize)
      if (batch.length === 0) {
        return Promise.resolve(allDevices)
      }
      
      
      return Promise.all(
        batch.map(user => 
          StrappService.getUserDevices(user.id, { 
            limit: 100,
            populate: 'user,user.address,model,site,billingEntity'
          })
            .then(response => {
              const devices = response.results || []
              return devices
            })
            .catch(error => {
              console.error(`  ❌ Error for user ${user.id}:`, error.message)
              return []
            })
        )
      )
        .then(devicesArrays => {
          const batchDevices = devicesArrays.flat()
          allDevices.push(...batchDevices)
          
          // Attendre 3 secondes entre les batchs pour respecter le rate limit (1000 req/min)
          // 50 req par batch * 20 batchs/min = 1000 req/min max
          if (startIndex + batchSize < users.length) {
            return new Promise(resolve => setTimeout(resolve, 3000))
              .then(() => processBatch(startIndex + batchSize))
          }
          
          return allDevices
        })
    }
    
    return processBatch(0)
  },

  /**
   * Charge tous les users Strapp avec pagination
   * @private
   * @returns {Promise<Array>} Tous les users
   */
  _fetchAllStrappUsers() {
    const allUsers = []
    
    const fetchPage = (cursor = undefined) => {
      return StrappService.getUsers({ limit: 100, cursor })
        .then(response => {
          const users = response.results || []
          allUsers.push(...users)
          
          // Si on a un cursor et qu'on n'a pas atteint la limite, continuer
          if (response.cursor && users.length === 100) {
            return fetchPage(response.cursor)
          }
          
          return allUsers
        })
    }
    
    return fetchPage()
  },

  /**
   * Récupère les devices d'un utilisateur Knox
   * @param {string} userId - ID utilisateur Knox
   * @returns {Promise<Array>} Devices de l'utilisateur
   */
  getDevicesByKnoxUser(userId) {
    return KnoxService.getUserDevices(userId)
      .then(knoxDevices => {
        const imeis = knoxDevices
          .map(device => device.imei)
          .filter(imei => imei)

        if (imeis.length === 0) {
          return knoxDevices.map(device => ({
            imei: device.imei,
            knoxData: device,
            strappData: null,
            strappSimData: null
          }))
        }

        return Promise.all(
          imeis.map(imei => 
            StrappService.getDevices({ 
              imei,
              limit: 1 
            })
              .then(response => response.data && response.data.length > 0 ? response.data[0] : null)
              .catch(() => null)
          )
        )
          .then(strappDevices => {
            return knoxDevices.map((knoxDevice, index) => {
              return this._mergeDeviceData(
                knoxDevice.imei,
                knoxDevice,
                strappDevices[index]
              )
            })
          })
      })
  },

  /**
   * Récupère les SIMs d'un utilisateur Strapp
   * @param {string} userId - ID utilisateur Strapp
   * @returns {Promise<Array>} Lignes SIM
   */
  getSIMsByStrappUser(userId) {
    return StrappService.getSIMs({ 
      user: userId
    })
      .then(response => response.data || [])
  },

  /**
   * Récupère les commandes d'un utilisateur
   * @param {string} userId - ID utilisateur Strapp
   * @returns {Promise<Array>} Commandes
   */
  getOrdersByUser(userId) {
    return StrappService.getOrders({})
      .then(response => {
        const orders = response.data || []
        return orders.filter(order => {
          return order.orderedBy === userId || 
                 (order.items?.some(item => item.userId === userId))
        })
      })
  },

  /**
   * Crée une commande de device(s)
   * @param {Object} orderData - Données commande
   * @returns {Promise<Object>} Commande créée
   */
  createOrder(orderData) {
    return StrappService.createOrder(orderData)
  },

  /**
   * Initie un rachat de device
   * @param {string} deviceId - ID device Strapp
   * @returns {Promise<Object>} Résultat
   */
  initiateBuyback(deviceId) {
    return StrappService.createBuyback(deviceId)
  },

  /**
   * Récupère la consommation d'une SIM
   * @param {Object} params - Paramètres (simId, iccid ou phoneNumber + dates)
   * @returns {Promise<Object>} Consommation
   */
  getSIMUsage(params) {
    return StrappService.getSIMUsage(params)
  },

  /**
   * Active une SIM
   * @param {Object} activationData - Données activation
   * @returns {Promise<Object>} Résultat
   */
  activateSIM(activationData) {
    return StrappService.activateSIM(activationData)
  },

  /**
   * Résilie une SIM
   * @param {Object} cancelData - Données résiliation
   * @returns {Promise<Object>} Résultat
   */
  cancelSIM(cancelData) {
    return StrappService.cancelSIM(cancelData)
  },

  /**
   * ACTIONS KNOX - Commandes device
   */

  /**
   * Verrouille un device Knox
   * @param {string} deviceId - ID device Knox
   * @param {string} message - Message affiché
   * @returns {Promise<Object>} Résultat
   */
  lockDevice(deviceId, message = '') {
    return KnoxService.lockDevice(deviceId, message)
  },

  /**
   * Déverrouille un device Knox
   * @param {string} deviceId - ID device Knox
   * @returns {Promise<Object>} Résultat
   */
  unlockDevice(deviceId) {
    return KnoxService.unlockDevice(deviceId)
  },

  /**
   * Redémarre un device Knox
   * @param {string} deviceId - ID device Knox
   * @returns {Promise<Object>} Résultat
   */
  rebootDevice(deviceId) {
    return KnoxService.rebootDevice(deviceId)
  },

  /**
   * Factory reset un device Knox (IRRÉVERSIBLE)
   * @param {string} deviceId - ID device Knox
   * @returns {Promise<Object>} Résultat
   */
  factoryResetDevice(deviceId) {
    return KnoxService.factoryReset(deviceId)
  },

  /**
   * Envoie une notification au device
   * @param {string} deviceId - ID device Knox
   * @param {string} title - Titre
   * @param {string} message - Message
   * @param {string} sendType - Type (Notification | Popup)
   * @returns {Promise<Object>} Résultat
   */
  sendNotification(deviceId, title, message, sendType = 'Notification') {
    return KnoxService.sendNotification(deviceId, title, message, sendType)
  },

  /**
   * Collecte la position GPS du device
   * @param {string} deviceId - ID device Knox
   * @returns {Promise<Object>} Position GPS
   */
  collectGPS(deviceId) {
    return KnoxService.collectGPS(deviceId)
  },

  /**
   * Collecte les informations hardware du device
   * @param {string} deviceId - ID device Knox
   * @returns {Promise<Object>} Informations hardware
   */
  collectDeviceInfo(deviceId) {
    return KnoxService.collectDeviceInfo(deviceId)
  },

  /**
   * Met à jour le profil agent/client du device
   * @param {string} deviceId - ID device Knox
   * @returns {Promise<Object>} Résultat
   */
  updateProfile(deviceId) {
    return KnoxService.updateProfile(deviceId)
  },

  /**
   * Retire le device de la gestion Knox (unenroll)
   * @param {string} deviceId - ID device Knox
   * @returns {Promise<Object>} Résultat
   */
  unenrollDevice(deviceId) {
    return KnoxService.unenrollDevice(deviceId)
  },

  /**
   * Récupère la position GPS actuelle
   * @param {string} deviceId - ID device Knox
   * @param {boolean} forceRefresh - Forcer refresh
   * @returns {Promise<Object>} Position GPS
   */
  getDeviceLocation(deviceId, forceRefresh = false) {
    return KnoxService.getDeviceLocation(deviceId, forceRefresh)
  },

  /**
   * Récupère l'historique GPS
   * @param {string} deviceId - ID device Knox
   * @param {string} startDate - Date début (ISO-8601)
   * @param {string} endDate - Date fin (ISO-8601)
   * @returns {Promise<Object>} Historique GPS
   */
  getDeviceLocationHistory(deviceId, startDate, endDate) {
    return KnoxService.getDeviceLocationHistory(deviceId, startDate, endDate)
  },

  /**
   * Installe une app sur le device
   * @param {string} deviceId - ID device Knox
   * @param {string} packageName - Package name
   * @param {string} version - Version (optionnel)
   * @returns {Promise<Object>} Résultat
   */
  installApp(deviceId, packageName, version = null) {
    return KnoxService.installApp(deviceId, packageName, version)
  },

  /**
   * Désinstalle une app
   * @param {string} deviceId - ID device Knox
   * @param {string} packageName - Package name
   * @returns {Promise<Object>} Résultat
   */
  uninstallApp(deviceId, packageName) {
    return KnoxService.uninstallApp(deviceId, packageName)
  },

  /**
   * Met à jour une app
   * @param {string} deviceId - ID device Knox
   * @param {string} packageName - Package name
   * @returns {Promise<Object>} Résultat
   */
  updateApp(deviceId, packageName) {
    return KnoxService.updateApp(deviceId, packageName)
  },

  /**
   * Récupère la liste des apps installées
   * @param {string} deviceId - ID device Knox
   * @param {Object} options - Options
   * @returns {Promise<Object>} Liste apps
   */
  getDeviceApps(deviceId, options = {}) {
    return KnoxService.getDeviceApps(deviceId, options)
  },

  /**
   * HELPERS - Fusion des données Knox + Strapp
   * @private
   */

  /**
   * Fusionne les données Knox et Strapp pour un device
   * @param {string} imei - IMEI du device
   * @param {Object} knoxData - Données Knox
   * @param {Object} strappData - Données Strapp
   * @returns {Object} Device fusionné
   */
  _mergeDeviceData(imei, knoxData, strappData) {
    return {
      imei,
      knoxDeviceId: knoxData ? knoxData.deviceId : null,
      strappDeviceId: strappData ? strappData.id : null,
      knoxData: knoxData || null,
      strappData: strappData || null,
      status: this._getDeviceStatus(knoxData, strappData),
      model: (strappData && strappData.model) || (knoxData && (knoxData.deviceModelKind || knoxData.deviceModel)) || null,
      user: this._getDeviceUser(knoxData, strappData),
      site: strappData ? strappData.site : null,
      billingEntity: strappData ? strappData.billingEntity : null,
      isEligibleForBuyback: strappData ? strappData.isEligibleForBuyback : false,
      isArchived: strappData ? strappData.isArchived : false,
      iccid: knoxData ? this._normalizeIccid(knoxData.iccid) : null,
      lastUpdated: this._getLastUpdated(knoxData, strappData),
      createdAt: moment().format('YYYY-MM-DD'),
      updatedAt: moment().format('YYYY-MM-DD')
    }
  },

  /**
   * Fusionne deux listes de devices (Knox et Strapp)
   * @param {Array} knoxDevices - Devices Knox
   * @param {Array} strappDevices - Devices Strapp
   * @returns {Array} Devices fusionnés
   */
  _mergeDevicesLists(knoxDevices, strappDevices) {
    const deviceMap = new Map()

    knoxDevices.forEach(knoxDevice => {
      if (knoxDevice.imei) {
        deviceMap.set(knoxDevice.imei, {
          knoxData: knoxDevice,
          strappData: null
        })
      }
    })

    strappDevices.forEach(strappDevice => {
      if (strappDevice.imei) {
        if (deviceMap.has(strappDevice.imei)) {
          deviceMap.get(strappDevice.imei).strappData = strappDevice
        } else {
          deviceMap.set(strappDevice.imei, {
            knoxData: null,
            strappData: strappDevice
          })
        }
      }
    })

    const mergedDevices = []
    deviceMap.forEach((value, imei) => {
      mergedDevices.push(
        this._mergeDeviceData(imei, value.knoxData, value.strappData)
      )
    })

    return mergedDevices
  },

  /**
   * Détermine le statut global du device
   * @param {Object} knoxData - Données Knox
   * @param {Object} strappData - Données Strapp
   * @returns {string} Statut
   */
  _getDeviceStatus(knoxData, strappData) {
    if (strappData?.status) {
      return strappData.status
    }
    if (knoxData?.deviceStatus) {
      const statusMap = {
        'I': 'Idle',
        'P': 'Pending',
        'A': 'Active',
        'B': 'Blocked',
        'BS': 'Blocked (Security)',
        'BA': 'Blocked (Admin)',
        'BL': 'Blocked (License)'
      }
      return statusMap[knoxData.deviceStatus] || knoxData.deviceStatus
    }
    return 'Unknown'
  },

  /**
   * Récupère l'utilisateur du device (Knox ou Strapp)
   * @param {Object} knoxData - Données Knox
   * @param {Object} strappData - Données Strapp
   * @returns {Object|string|null} Utilisateur
   */
  _getDeviceUser(knoxData, strappData) {
    if (strappData?.user) {
      return strappData.user
    }
    if (knoxData?.userName) {
      return {
        id: knoxData.userId,
        name: knoxData.userName,
        source: 'knox'
      }
    }
    return null
  },

  /**
   * Récupère la date de dernière mise à jour
   * @param {Object} knoxData - Données Knox
   * @param {Object} strappData - Données Strapp
   * @returns {string} Date (YYYY-MM-DD)
   */
  _getLastUpdated(knoxData, strappData) {
    const dates = []
    
    if (knoxData?.lastUpdated) {
      dates.push(moment(knoxData.lastUpdated))
    }
    if (strappData?.updatedAt) {
      dates.push(moment(strappData.updatedAt))
    }

    if (dates.length === 0) {
      return moment().format('YYYY-MM-DD')
    }

    return moment.max(dates).format('YYYY-MM-DD')
  },

  /**
   * Normalise l'ICCID en enlevant le dernier caractère si besoin
   * Les ICCID standards font 19 ou 20 chiffres. Le 20ème est souvent un checksum.
   * Knox peut retourner 19 ou 20 caractères, Strapp peut avoir un suffixe alphabétique.
   * @param {string} iccid - ICCID brut
   * @returns {string|null} ICCID normalisé (19 chiffres) ou null
   */
  _normalizeIccid(iccid) {
    if (!iccid) {
      return null
    }
    
    // Enlever tous les caractères non-numériques à la fin
    // Exemple: "8933011234567890123A" -> "8933011234567890123"
    //          "89330112345678901230" -> "89330112345678901230"
    const cleanedIccid = iccid.replace(/[^0-9]+$/, '')
    
    // Si l'ICCID fait 20 chiffres ou plus, prendre les 19 premiers
    const normalized = cleanedIccid.length >= 20 ? cleanedIccid.slice(0, 19) : cleanedIccid
    
    return normalized
  }
}
